import { ScreenshareModePicker } from "@/app/app-components/ScreenshareModePicker/ScreenshareModePicker";
import { MGButton } from "@/components/buttons";
import { MGDaysPickerField } from "@/components/controls";
import {
  MGAutocomplete,
  MGDropdownField,
  MGDropdownItem,
  MGInputListField,
  MGTextAreaField,
  MGTextboxField,
} from "@/components/inputs";
import { MGColorPickerField } from "@/components/inputs/MGColorPicker";
import { MGFlexBox, MGFlexBoxSpacer } from "@/components/layout";
import { MGModal, MGModalBody, MGModalFooter, MGModalHeader } from "@/components/modals";
import { MGTimePickerField } from "@/components/pickers";
import { MGProgressSpinner } from "@/components/progress";
import { MGSpacing } from "@/components/spacing";
import { MGHeading2, MGHelperText, MGLabel } from "@/components/typography";

import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export const ConfigureClassForm = (props) => {
  const {
    initialValues,
    onSubmit,
    classDurations,
    isShowing,
    loading,
    loadingViewClass,
    onCancel,
    formHeading,
    getFilteredTeachers,
    debounceSearch,
  } = props;

  const { t } = useTranslation();

  const minuteToMillisecondsFactor = 1000 * 60;

  const submissionHandler = (values) => {
    let teacherIds = [];

    values.teachers?.map((teacher) => {
      teacherIds.push(teacher.id);
    });
    values.teacher_ids = teacherIds;

    //values.teachers does not exist on the payload required for addclass mutation.
    //It gets added to the form, and then removed before being returned to pageState.js
    let submissionValues = Object.assign({}, values);
    delete submissionValues.teachers;
    onSubmit(submissionValues);
  };

  const validationSchema = yup.object({
    name: yup.string().min(3).required(t("Required")),
    duration: yup.number().required(t("Required")),
    screen_share_enabled: yup.string().required(t("Required")),
    schedules: yup.object({
      start_time: yup.date(),
      end_time: yup.date().test(
        "end-after-start",
        t("End time must be later than start time"),
        function (value) {
          const { start_time } = this.parent;
          if (!start_time || !value) return true;
          return new Date(value) >= new Date(start_time);
        }
      ),
    }),
    description: yup.string().required(t("Required")),
  });

  return (
    <MGModal isShowing={isShowing}>
      {loadingViewClass && <MGProgressSpinner isLarge={true} />}
      {!loadingViewClass && (
        <Formik initialValues={initialValues} onSubmit={submissionHandler} validationSchema={validationSchema}>
          {({ dirty, isValid, submitForm, values, setFieldValue, setFieldTouched }) => {
            let filteredTeachers = getFilteredTeachers(values);

            const [teacherSearchText, setTeacherSearchText] = useState("");

            const addItemToList = (item) => {
              // Selecting a suggestion passes the full teacher object, so same-named
              // teachers resolve to the right person by id. The add button / Enter key
              // pass no item, so fall back to matching the typed text against the name.
              const teacher =
                item && typeof item === "object"
                  ? item
                  : filteredTeachers?.find((t) => t?.full_name === (item || teacherSearchText));

              if (teacher) {
                const currentTeachers = values?.teachers ?? [];
                if (!currentTeachers.some((t) => t?.id === teacher.id)) {
                  setFieldValue("teachers", [...currentTeachers, teacher]);
                  setFieldTouched("teachers", true);
                }
                setTimeout(() => {
                  debounceSearch("");
                  setTeacherSearchText("");
                }, 100);
              }
            };

            const onDurationChangeHandler = (duration) => {
              setFieldValue(
                "schedules.end_time",
                new Date(
                  Math.round(new Date(values.schedules.start_time).getTime() + duration * minuteToMillisecondsFactor)
                )
              );
            };

            const onStartTimeChangeHandler = (startTime) => {
              setFieldValue(
                "schedules.end_time",
                new Date(Math.round(new Date(startTime).getTime() + values.duration * minuteToMillisecondsFactor))
              );
            };

            return (
              <>
                <MGModalHeader>
                  <MGHeading2>{formHeading}</MGHeading2>
                </MGModalHeader>
                <MGModalBody>
                  <Form noValidate autoComplete={"off"}>
                    <MGLabel htmlFor="name">{t("Class Name")}</MGLabel>
                    <MGTextboxField name="name" placeholder={t("Enter Class Name")} />
                    <MGLabel>{t("Teachers")}</MGLabel>
                    <MGAutocomplete
                      value={teacherSearchText}
                      onChange={(text) => {
                        setTeacherSearchText(text);
                        debounceSearch(text);
                      }}
                      placeholder={loading ? t("Loading...") : t("Search for teacher")}
                      suggestions={filteredTeachers}
                      displayProp={"full_name"}
                      suggestionRenderer={(teacher) => (
                        <div>
                          <div>{teacher?.full_name}</div>
                          {teacher?.email && <MGHelperText>{teacher.email}</MGHelperText>}
                        </div>
                      )}
                      actionButtonIcon={"add"}
                      onActionButtonClick={addItemToList}
                      enterKeyFiresAction={true}
                      onItemSelected={addItemToList}
                    />

                    <br />
                    <MGInputListField
                      name={"teachers"}
                      displayExpr={(teacher) => {
                        // Some flows seed selected teachers as plain name strings; render
                        // those as-is. Otherwise show the email (when present) alongside the
                        // name so same-named teachers stay distinguishable in the chip list.
                        if (typeof teacher === "string") return teacher;
                        return teacher?.email
                          ? `${teacher?.full_name} (${teacher.email})`
                          : teacher?.full_name;
                      }}
                      onItemRemoved={(item) => {
                        let newTeachers = [];
                        values?.teachers?.map((teacher) => {
                          if (teacher?.id !== item.id) {
                            newTeachers.push(teacher);
                          }
                          setFieldValue("teachers", newTeachers);
                          setFieldTouched("teachers", true);
                        });
                      }}
                    />
                    <MGLabel>{t("Set duration for class")}</MGLabel>
                    <MGDropdownField
                      name="duration"
                      placeholder={t("Class duration")}
                      onChange={onDurationChangeHandler}
                    >
                      {classDurations &&
                        classDurations.map((duration) => (
                          <MGDropdownItem key={duration.value} value={duration.value}>
                            {duration.content}
                          </MGDropdownItem>
                        ))}
                    </MGDropdownField>
                    <MGLabel>{t("Screen previews")}</MGLabel>
                    <ScreenshareModePicker
                      name={"screen_share_enabled"}
                      placeholder={t("Select screen previews setting")}
                    />
                    <MGLabel>{t("Set schedule")}</MGLabel>
                    <MGFlexBox justify="start">
                      <div>
                        <MGHelperText>{t("Start time")}</MGHelperText>
                        <MGTimePickerField name="schedules.start_time" onChange={onStartTimeChangeHandler} />
                      </div>
                      <MGSpacing size="md" />
                      <div>
                        <MGHelperText>{t("End time")}</MGHelperText>
                        <MGTimePickerField name="schedules.end_time" />
                      </div>
                    </MGFlexBox>
                    <MGFlexBox>
                      <div>
                        <MGLabel>{t("Select days of the week")}</MGLabel>
                        <br />
                        <MGDaysPickerField name="schedules.weekdays" />
                      </div>
                      <MGSpacing size={"xl"} />
                      <MGLabel htmlFor="class_color">{t("Class color")}</MGLabel>
                      <MGSpacing size={"md"} />
                      <div>
                        <MGColorPickerField name="class_color" inline={true} />
                      </div>
                      <MGFlexBoxSpacer />
                    </MGFlexBox>
                    <MGLabel>{t("Description")}</MGLabel>
                    <MGTextAreaField name="description" width={"100%"} height={"120px"} />
                  </Form>
                </MGModalBody>
                <MGModalFooter>
                  <center>
                    <MGButton variant="secondary" onClick={onCancel}>
                      {t("Cancel")}
                    </MGButton>
                    <MGSpacing size={"md"} />
                    <MGButton onClick={submitForm} variant="primary" isLoading={loading}>
                      {t("Save")}
                    </MGButton>
                  </center>
                </MGModalFooter>
              </>
            );
          }}
        </Formik>
      )}
    </MGModal>
  );
};

export default ConfigureClassForm;
